import interactionResponse from './interaction-response';

// This function takes an array of tasks and runs them one at a time, yielding to the main thread between each task.
export const yieldTasks = async (tasks: (() => void)[]) => {
    // Loop over the tasks:
    while (tasks.length > 0) {
        // Shift the first task off the tasks array:
        const task = tasks.shift();

        if (task) {
            // Run the task:
            task();

            // Allow time for paint to reduce INP (Interaction to Next Paint)
            await interactionResponse();
        }
    }
};

// Upper bound on the post-load idle wait for deferred vendor work, so a main thread
// that never goes idle delays the vendors (and the attribution they carry) by
// seconds, not forever.
export const DEFERRED_VENDOR_IDLE_TIMEOUT_MS = 4000;

// A page whose `load` event never fires (a hung subresource, a stream that never
// closes) must not keep deferred work from ever running.
const LOAD_EVENT_FALLBACK_MS = 15_000;

/**
 * Runs `task` once, after the window `load` event and the next idle period —
 * the earliest point that reliably sits past LCP. For work that must not compete
 * with first render (vendor scripts, analytics SDKs). `timeout` bounds the idle
 * wait so a busy main thread delays the task rather than starving it.
 */
export const runAfterLoadAndIdle = (task: () => void, { timeout }: { timeout: number }): void => {
    if (typeof window === 'undefined') return;

    const requestIdle = (): void => {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(() => task(), { timeout });
        } else {
            window.setTimeout(task, 0);
        }
    };

    if (document.readyState === 'complete') {
        requestIdle();
        return;
    }

    // Whichever of the two fires first cancels the other.
    const onLoadOrFallback = (): void => {
        window.removeEventListener('load', onLoadOrFallback);
        window.clearTimeout(fallbackHandle);
        requestIdle();
    };
    const fallbackHandle = window.setTimeout(onLoadOrFallback, LOAD_EVENT_FALLBACK_MS);
    window.addEventListener('load', onLoadOrFallback, { once: true });
};
